import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { methodValidator } from 'features/commissionCalculationMethods/validators';
import { SAVE_COMMISSION_CALCULATION_METHOD } from '../../mutations';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { COMMISSION_CALCULATION_METHOD_SAVED } from '../../subscriptions';
import { Prompt } from 'react-router-dom';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { Autocomplete, TextField, Button } from '@totalsoft/rocket-ui';
import { methodModalVar } from 'features/commissionCalculationMethods/variables';
import { COMMISSION_CALCULATION_METHOD_TYPES_QUERY } from 'features/commissionCalculationMethods/queries';
import AllowedFormulaVariablesComponent from './AllowedFormulaVariablesComponent';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { commissionCalculationMethodsStore } from '../ListBrowserComponent';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_COMMISSION_CALCULATION_METHOD_WRITE } from 'constants/rightsCodes';

const MethodComponent = ({ method }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const { store } = useListBrowserUtils(commissionCalculationMethodsStore);
  const canWrite = usePermission(ADMIN_COMMISSION_CALCULATION_METHOD_WRITE);

  const [saving, setSaving] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [validation, validate] = useDirtyFieldValidation(methodValidator);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  // #region Method types

  const { data: methodTypesData } = useQuery(COMMISSION_CALCULATION_METHOD_TYPES_QUERY);

  const methodTypes = useMemo(
    () => (methodTypesData && methodTypesData.commissionCalculationMethodTypes.filter(x => x.code !== 'System')) || [],
    [methodTypesData]
  );

  const systemType = useMemo(
    () => methodTypesData && methodTypesData.commissionCalculationMethodTypes.find(x => x.code === 'System'),
    [methodTypesData]
  );

  const isSystem = useMemo(() => get(lens.typeId) === systemType?.id, [lens, systemType]);

  // #endregion

  // #region Side effects

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    const { typeName, ...rest } = method;
    resetLens(rest);
  }, [method, resetLens]);

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #endregion

  // #region Save

  const [saveMethod] = useMutation(SAVE_COMMISSION_CALCULATION_METHOD, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const method = get(lens);
    if (!validate(method)) return;
    setSaving(true);
    saveMethod({ variables: { input: { ...method } } });
  }, [lens, validate, saveMethod]);

  useSubscription(COMMISSION_CALCULATION_METHOD_SAVED, {
    onData: _ => {
      addToast(t('CommissionCalculationMethod.Saved'), 'success');
      methodModalVar({ open: false });
      store.refetch();
    }
  });

  // #endregion

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            value={get(lens.name) || ''}
            onChange={event => set(lens.name)(event)}
            label={t('CommissionCalculationMethod.Name')}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            value={get(lens.code) || ''}
            onChange={event => set(lens.code)(event)}
            label={t('CommissionCalculationMethod.Code')}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          {isSystem ? (
            <TextField fullWidth disabled value={systemType?.name || ''} label={t('CommissionCalculationMethod.Type')} />
          ) : (
            <Autocomplete
              id='commissionCalculationMethodFilter_typeId'
              disabled={!canWrite}
              fullWidth
              isSearchable
              isClearable
              simpleValue
              required
              valueKey={'id'}
              labelKey={'name'}
              label={t('CommissionCalculationMethod.Type')}
              value={get(lens.typeId) || ''}
              onChange={set(lens.typeId)}
              options={methodTypes}
              error={!isValid(validation.typeId)}
              helperText={getErrors(validation.typeId)}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            disabled={!canWrite}
            control={
              <Checkbox
                checked={get(lens.percentIsMandatory) || false}
                onChange={event => set(lens.percentIsMandatory)(event.target.checked)}
                color='primary'
              />
            }
            label={t('CommissionCalculationMethod.PercentIsMandatory')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            disabled={!canWrite}
            control={
              <Checkbox
                checked={get(lens.assetIsMandatory) || false}
                onChange={event => set(lens.assetIsMandatory)(event.target.checked)}
                color='primary'
              />
            }
            label={t('CommissionCalculationMethod.AssetIsMandatory')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            disabled={!canWrite}
            control={
              <Checkbox
                checked={get(lens.ppIsMandatory) || false}
                onChange={event => set(lens.ppIsMandatory)(event.target.checked)}
                color='primary'
              />
            }
            label={t('CommissionCalculationMethod.PPIsMandatory')}
          />
        </Grid>
        <Grid item xs={12}>
          <AllowedFormulaVariablesComponent />
        </Grid>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            multiline
            value={get(lens.amountFormula) || ''}
            onChange={event => set(lens.amountFormula)(event)}
            label={t('CommissionCalculationMethod.AmountFormula')}
            error={!isValid(validation.amountFormula)}
            helperText={getErrors(validation.amountFormula)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            multiline
            value={get(lens.percentFormula) || ''}
            onChange={event => set(lens.percentFormula)(event)}
            label={t('CommissionCalculationMethod.PercentFormula')}
            error={!isValid(validation.percentFormula)}
            helperText={getErrors(validation.percentFormula)}
          />
        </Grid>
        <Grid container item xs={12} justifyContent='flex-end' gap={1}>
          <Button onClick={handleSave} disabled={saving || !canWrite}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => methodModalVar({ open: false })} disabled={saving}>
            {t('Close')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

MethodComponent.propTypes = {
  method: PropTypes.object
};

export default MethodComponent;
